/**
 * deployment-detection-edit.js
 */

import React from 'react';
import { 
  useMediaQuery,
  useTheme,
  Box,
  Grid, 
  Typography, 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Types } from 'utils/constants';
import ArrestsEdit from 'components/deployment/helpers/arrests-edit';
import DetectionOdorsEdit from 'components/detection-odors/edit';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import { EditorIconSection } from 'components/icon-section/icon-section';
import LabeledSwitch from 'components/labeled-switch';
import Select from 'components/form-inputs/select';
import Switch from 'components/form-inputs/switch';
import TextField from 'components/form-inputs/text-field';
import FormSectionTitle from 'components/menu-drawer/form-section-title';


const useStyles = makeStyles(theme => ({
  arrestsTitle: {
    marginTop: theme.spacing(2.125),
  },
  helpInfoClarifier: {
    marginTop: theme.spacing(2),
    fontSize: theme.typography.sizes[1],
  },
}));


export default function DeploymentDetectionEdit(props) {
  const {
    drawerState,
    edit,
  } = props;
  const { t } = useTranslation(edit.translation);
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme => theme.size.menuDrawer.mediaRuleMobileContent, { noSsr: true });
  const { control, setValue } = useFormContext();
  const notIndicatedCurrencyValue = useWatch({ control, name: 'detection.wasNotIndicatedCurrencyPresent' });
  const notIndicatedCurrencyUnit = useWatch({ control, name: 'detection.notIndicatedCurrencyType' });
  const odorlessEnvExists = useWatch({ control, name: 'detectionOdorlessEnvironmentExists'});
  const defaultCurrencyUnit = edit.options.currencyTypes[0];


  // set default not-indicated currency type if none
  React.useEffect(() => {
    if (notIndicatedCurrencyValue && notIndicatedCurrencyUnit === null) {
      setValue('detection.notIndicatedCurrencyType', defaultCurrencyUnit);
    }
  }, [notIndicatedCurrencyValue, notIndicatedCurrencyUnit, defaultCurrencyUnit, setValue]);
  

  // Set didSubjectAdmitToPossession to null if odorlessEnvExists becomes false
  React.useEffect(() => {
    if (odorlessEnvExists === false) {
      setValue('detection.didSubjectAdmitToPossession', null);
    }
  }, [odorlessEnvExists, setValue]);


  return (
    <>
      {/* shift detection editor left on mobile for space requirements */}
      <Box 
        sx={theme => ({ 
          [theme.breakpoints.down('sm')]: {
            '& .pt-icon-section-content': {
              marginLeft: '-32px',
              maxWidth: `calc(100vw - 58px)`,
            }
          },
        })}
      >
        {/* searched areas / indications / odors form */}
        <EditorIconSection
          iconName='environment'
          iconMarginTop={6}
          helpProps={{
            translation: edit.translation,
            section: 'environments'
          }}
        >
          <DetectionOdorsEdit
            name='detection.environments'
            recordType={Types.Record.DEPLOYMENT}
            drawerState={drawerState}
            translation={edit.translation}
            options={edit.options}
          />


          {/* not-indicated currency present */}
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.notIndicatedCurrency.helpTitle')}
            text={t('environments.notIndicatedCurrency.helpInfo')}
            style={{ marginTop: theme.spacing(4), marginLeft: isMobile ? theme.spacing(-8.5) : 'revert-layer', paddingBottom: 12 }}
          >
            {focusHandlers =>
            <>
              <Typography>{t('environments.notIndicatedCurrency.label')}</Typography>
              <Switch
                name='detection.wasNotIndicatedCurrencyPresent'
                component={LabeledSwitch}
                style={{ marginTop: 8, marginBottom: 0 }}
              />

              {notIndicatedCurrencyValue &&
              <>
                <div style={{ display: 'flex', marginTop: 12 }}>
                  <TextField
                    name='detection.notIndicatedCurrencyAmount'
                    label={t('environments.notIndicatedCurrencyAmount.label')}
                    type='number'
                    focusHandlers={focusHandlers}
                    style={{ maxWidth: 180, marginRight: 16 }}
                    rules={{ 
                      required: true,
                    }}
                  />
                  <Select
                    name='detection.notIndicatedCurrencyType'
                    options={edit.options.currencyTypes}
                    setValueAs={id => ({ id })}
                    style={{ maxWidth: 80, marginTop: 4, marginBottom: 1 }}
                  />
                </div>
              </>}
            </>}
          </HelpTextItem>


          {/* did subject admit to possession */}
          {odorlessEnvExists &&
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.didSubjectAdmitToPossession.helpTitle')}
            text={t('environments.didSubjectAdmitToPossession.helpInfo')}
            style={{ marginTop: 8, marginLeft: isMobile ? theme.spacing(-8.5) : 'revert-layer', paddingBottom: 12 }}
          >
            <Typography>{t('environments.didSubjectAdmitToPossession.label')}</Typography>
            <Switch
              name='detection.didSubjectAdmitToPossession'
              component={LabeledSwitch}
              style={{ marginTop: 8, marginBottom: 1 }}
            />
          </HelpTextItem>}

        </EditorIconSection>
      </Box>


      {/* arrests editor */}
      <EditorIconSection
        iconName='patrol-arrests-bites'
        iconMarginTop={8}
        helpProps={{
          translation: edit.translation,
          section: 'arrests'
        }}
      >
        {!isMobile && <FormSectionTitle className={classes.arrestsTitle}>{t('arrests.sectionName')}</FormSectionTitle>}

        <Grid item xs={12}>
          <Typography variant='subtext-small' sx={{ mt: 1 }}>
            {t('detection.dogAssistedArrests.helpInfoClarifier')}
          </Typography>
        </Grid>

        <ArrestsEdit
          {...props}
          formParentDataName={'detection'}
          workMode={Types.WorkMode.DETECTION}
        />
      </EditorIconSection>
    </>
  );
}